/**
 * LES DIAGRAMMES STATISTIQUES — capacité `CAP-08` (tranche T8 du chantier
 * « mathématiques de bout en bout »).
 *
 * ─── Un format de DONNÉES, pas de dessin ────────────────────────────────────
 *
 * L'auteur écrit ce que le diagramme REPRÉSENTE — les effectifs, les classes,
 * les cinq nombres d'un diagramme de quartiles, les points d'un nuage. Il
 * n'écrit ni coordonnée d'écran, ni couleur, ni graduation : c'est ce module
 * qui calcule l'échelle, les axes et le tracé. Un diagramme ne peut donc pas
 * mentir sur ses données (une bande trop haute, un secteur trop large).
 *
 * Six diagrammes : `bandes`, `ligne_brisee`, `circulaire`, `histogramme`,
 * `quartiles`, `nuage`. Le tableau de distribution est la table de `CAP-09`.
 *
 * ─── Le moteur des fiches, pas un autre ─────────────────────────────────────
 *
 * `resoudreDiagramme` rend un `DessinResolu` : la liste de traces que
 * l'application (`DessinVue`) et la console (`DessinWeb`) savent DÉJÀ peindre,
 * avec les rôles de couleur des fiches, le défilement et l'agrandissement au
 * téléphone. Aucun second moteur de dessin.
 *
 * ─── Trois emplois, un seul objet ───────────────────────────────────────────
 *
 *  - AFFICHÉ dans une fiche (illustration de genre `diagramme`) et dans un
 *    énoncé (`exercice.diagramme`) ;
 *  - À CONSTRUIRE : la nature de réponse `diagramme`. Chaque diagramme est une
 *    liste d'ÉLÉMENTS à valeur numérique (`elementsDe`) — la hauteur d'une
 *    bande, l'effectif d'une classe, l'un des cinq nombres, l'ordonnée d'un
 *    point. L'élève en écrit certains ; le diagramme se redessine sous ses
 *    yeux ; la correction juge chaque élément, comme la table de `CAP-09`.
 *
 * Une valeur `null` est un élément PAS ENCORE DONNÉ : il se dessine en creux
 * (un contour, un « ? »), jamais à une hauteur inventée.
 *
 * Jamais la couleur seule : les secteurs d'un diagramme circulaire sont
 * NUMÉROTÉS, et la légende reprend les numéros.
 */
import { lireRationnel } from '../math/ensembles.js';
import { LARGEUR_TOILE } from '../types/fiche-cours.js';
/* ───────────────────────────────── le format ───────────────────────────────── */
export const TYPES_DE_DIAGRAMME = [
    'bandes',
    'ligne_brisee',
    'circulaire',
    'histogramme',
    'quartiles',
    'nuage',
    /* `CAP-12` (tranche T10) : le hasard et le dénombrement — mêmes données, même moteur. */
    'arbre',
    'venn',
    'graphe',
];
/** Les régions d'un diagramme de Venn, dans l'ordre de ses éléments. `A`, `B`, `C` : les ensembles, dans l'ordre. */
export const REGIONS_DE_VENN = {
    2: ['A', 'B', 'AB', 'dehors'],
    3: ['A', 'B', 'C', 'AB', 'AC', 'BC', 'ABC', 'dehors'],
};
/** Une valeur portée sur un axe affiché : sa coordonnée de toile. */
export function versToile(axe, v) {
    return axe.debut + ((v - axe.de) / (axe.a - axe.de)) * (axe.fin - axe.debut);
}
/** Une coordonnée de toile ramenée à la valeur de l'axe. */
export function depuisToile(axe, t) {
    return axe.de + ((t - axe.debut) / (axe.fin - axe.debut)) * (axe.a - axe.de);
}
const CLES_QUARTILES = ['min', 'q1', 'mediane', 'q3', 'max'];
const NOMS_QUARTILES = [
    'le minimum',
    'le premier quartile',
    'la médiane',
    'le troisième quartile',
    'le maximum',
];
const fr = (n) => {
    const arrondi = Math.round(n * 1000) / 1000;
    return String(arrondi).replace('.', ',').replace('-', '−');
};
/** Chaque branche de l'arbre, dans l'ordre de lecture (de haut en bas, une branche avant ses suites). */
function branchesDe(branches, avant = []) {
    return branches.flatMap((b) => [
        { b, chemin: [...avant, b] },
        ...branchesDe(b.branches ?? [], [...avant, b]),
    ]);
}
const estFeuille = (b) => (b.branches ?? []).length === 0;
/** Une fraction exacte, écrite : « 6/25 », « 1 ». */
const ecrireFraction = (r) => (r.d === 1 ? String(r.n) : `${r.n}/${r.d}`);
const pgcd = (a, b) => (b === 0 ? Math.abs(a) : pgcd(b, a % b));
/** La probabilité d'une issue : le PRODUIT le long de son chemin. `null` dès qu'une branche manque. */
function produitDuChemin(chemin) {
    let n = 1;
    let d = 1;
    for (const b of chemin) {
        const r = b.p === null ? null : lireRationnel(b.p);
        if (r === null)
            return null;
        n *= r.n;
        d *= r.d;
        const g = pgcd(n, d) || 1;
        n /= g;
        d /= g;
    }
    return ecrireFraction({ n, d });
}
/** Ce que porte le bout d'un chemin : la valeur servie quand elle existe, sinon le produit calculé. */
const valeurDIssue = (chemin) => {
    const feuille = chemin[chemin.length - 1];
    return feuille.produit !== undefined ? feuille.produit : produitDuChemin(chemin);
};
const NOMS_DE_REGION = (e) => e.length === 2
    ? {
        A: `« ${e[0]} » seulement`,
        B: `« ${e[1]} » seulement`,
        AB: `« ${e[0]} » et « ${e[1]} »`,
        dehors: 'ni l’un ni l’autre',
    }
    : {
        A: `« ${e[0]} » seulement`,
        B: `« ${e[1]} » seulement`,
        C: `« ${e[2]} » seulement`,
        AB: `« ${e[0]} » et « ${e[1]} », sans « ${e[2]} »`,
        AC: `« ${e[0]} » et « ${e[2]} », sans « ${e[1]} »`,
        BC: `« ${e[1]} » et « ${e[2]} », sans « ${e[0]} »`,
        ABC: 'les trois à la fois',
        dehors: 'aucun des trois',
    };
const regionsDe = (ensembles) => ensembles.length === 3 ? REGIONS_DE_VENN[3] : REGIONS_DE_VENN[2];
/** Les éléments d'un diagramme, dans l'ordre de ses données. C'est eux qu'un élève complète. */
export function elementsDe(d) {
    switch (d.type) {
        case 'bandes':
        case 'ligne_brisee':
        case 'circulaire':
            return d.categories.map((nom, i) => ({ nom: `« ${nom} »`, valeur: d.valeurs[i] ?? null }));
        case 'histogramme':
            return d.effectifs.map((valeur, i) => ({
                nom: `la classe [${fr(d.bornes[i] ?? 0)} ; ${fr(d.bornes[i + 1] ?? 0)}[`,
                valeur,
            }));
        case 'quartiles':
            return CLES_QUARTILES.map((cle, i) => ({ nom: NOMS_QUARTILES[i], valeur: d[cle] }));
        case 'nuage':
            return d.points.map((p) => ({ nom: `le point d’abscisse ${fr(p.x)}`, valeur: p.y }));
        case 'arbre': {
            /* D'abord chaque branche, puis — quand l'arbre les écrit — chaque issue. */
            const toutes = branchesDe(d.branches);
            const nomDuChemin = (chemin) => chemin.map((b) => b.issue).join(', ');
            return [
                ...toutes.map(({ b, chemin }) => ({
                    nom: chemin.length === 1
                        ? `la branche « ${b.issue} »`
                        : `la branche « ${b.issue} » après ${nomDuChemin(chemin.slice(0, -1))}`,
                    valeur: b.p,
                })),
                ...(d.produits === true
                    ? toutes
                        .filter(({ b }) => estFeuille(b))
                        .map(({ chemin }) => ({
                        nom: `l’issue (${nomDuChemin(chemin)})`,
                        valeur: valeurDIssue(chemin),
                    }))
                    : []),
            ];
        }
        case 'venn': {
            const noms = NOMS_DE_REGION(d.ensembles);
            return regionsDe(d.ensembles).map((cle) => ({
                nom: noms[cle],
                valeur: d.regions[cle] ?? null,
            }));
        }
        case 'graphe':
            return [];
    }
}
/**
 * CE QUE L'ÉLÈVE A ÉCRIT pour un élément, tel qu'on le lui RELIT : sans source LaTeX. Le clavier
 * mathématique écrit `\\dfrac{2}{4}` et `5{,}5` ; la liste des éléments d'un diagramme corrigé est du TEXTE, pas
 * une formule — elle montrait donc la source (vu en recette navigateur, T12). `2/4`, `5,5`.
 */
export function ecrireSaisie(ecrit) {
    let s = ecrit.trim();
    for (let i = 0; i < 4; i += 1) {
        s = s.replace(/\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, '$1/$2');
    }
    return s
        .replace(/\{,\}/g, ',')
        .replace(/\\(?:left|right|,|;|!)/g, '')
        .replace(/\\cdot|\\times/g, '×')
        .replace(/-/g, '−')
        .trim();
}
/** Une valeur d'élément, écrite : un nombre à la française, une fraction telle quelle. */
export function ecrireValeur(v) {
    return typeof v === 'number' ? fr(v) : v.replace('.', ',');
}
/**
 * Ce que l'élève a ÉCRIT pour un élément, sous la forme que le diagramme
 * garde : un nombre pour une mesure ou un effectif, une fraction exacte pour une
 * probabilité. `null` : ce n'est pas un nombre.
 */
export function valeurConstruite(d, ecrit) {
    const lu = lireRationnel(ecrit);
    if (lu === null)
        return null;
    return d.type === 'arbre' ? ecrireFraction(lu) : lu.n / lu.d;
}
/** Le même diagramme, ses éléments remplacés un à un (`undefined` : inchangé). */
export function avecElements(d, valeurs) {
    const v = (i, avant) => {
        const x = valeurs[i];
        return x === undefined ? avant : typeof x === 'string' ? null : x;
    };
    switch (d.type) {
        case 'arbre': {
            const t = (i, avant) => {
                const x = valeurs[i];
                return x === undefined ? avant : x === null ? null : String(x);
            };
            const nbBranches = branchesDe(d.branches).length;
            let rangBranche = 0;
            let rangIssue = 0;
            const refaire = (branches, avant) => branches.map((b) => {
                const p = t(rangBranche, b.p);
                rangBranche += 1;
                const chemin = [...avant, b];
                if (!estFeuille(b))
                    return { ...b, p, branches: refaire(b.branches ?? [], chemin) };
                if (d.produits !== true)
                    return { ...b, p };
                const produit = t(nbBranches + rangIssue, valeurDIssue(chemin));
                rangIssue += 1;
                return { ...b, p, produit };
            });
            return { ...d, branches: refaire(d.branches, []) };
        }
        case 'venn':
            return {
                ...d,
                regions: Object.fromEntries(regionsDe(d.ensembles).map((cle, i) => [cle, v(i, d.regions[cle] ?? null)])),
            };
        case 'graphe':
            return d;
        case 'bandes':
        case 'ligne_brisee':
            return {
                ...d,
                valeurs: d.valeurs.map((x, i) => v(i, x)),
                ...axeGarde(d.axeValeurs, d.valeurs),
            };
        case 'circulaire': {
            /* Le total se lit sur le diagramme COMPLET, une fois, et ne se recalcule plus. */
            const total = d.total ?? totalDuCirculaire(d.valeurs);
            return {
                ...d,
                valeurs: d.valeurs.map((x, i) => v(i, x)),
                ...(total === null ? {} : { total }),
            };
        }
        case 'histogramme':
            return {
                ...d,
                effectifs: d.effectifs.map((x, i) => v(i, x)),
                ...axeGarde(d.axeValeurs, d.effectifs),
            };
        case 'quartiles':
            return {
                ...d,
                min: v(0, d.min),
                q1: v(1, d.q1),
                mediane: v(2, d.mediane),
                q3: v(3, d.q3),
                max: v(4, d.max),
            };
        case 'nuage':
            return {
                ...d,
                points: d.points.map((p, i) => ({ x: p.x, y: v(i, p.y) })),
                ...axeGarde(d.axeValeurs, d.points.map((p) => p.y)),
            };
    }
}
/**
 * L'étendue de l'axe à garder : celle déjà posée, sinon celle du diagramme
 * COMPLET (aucune valeur absente) ; rien si une valeur manque déjà et qu'aucune
 * étendue n'a été posée.
 */
function axeGarde(deja, valeurs) {
    if (deja !== undefined)
        return { axeValeurs: deja };
    const connues = valeurs.filter((x) => x !== null && Number.isFinite(x));
    if (connues.length !== valeurs.length || connues.length === 0)
        return {};
    /* L'échelle ronde que `cadre` gradue pour le diagramme complet — et UNE GRADUATION DE
       PLUS quand la plus grande valeur (ou la plus petite, négative) tombe exactement sur
       le bout de l'axe : sinon le haut de l'axe servi dirait la réponse (effectifs 4, 9,
       12, 5, 2 : un axe de 0 à 12). */
    const [min, max] = [Math.min(...connues), Math.max(...connues)];
    const e = echelleRonde(Math.min(0, min), Math.max(1, max));
    const juste = (x, bout) => Math.abs(x - bout) < e.pas * 1e-9;
    return {
        axeValeurs: {
            de: min < 0 && juste(min, e.bas) ? e.bas - e.pas : e.bas,
            a: juste(max, e.haut) ? e.haut + e.pas : e.haut,
            pas: e.pas,
        },
    };
}
/** La somme des parts d'un diagramme circulaire COMPLET ; `null` s'il lui en manque une, ou si elle est nulle. */
function totalDuCirculaire(valeurs) {
    if (valeurs.some((x) => x === null))
        return null;
    const somme = valeurs.reduce((t, x) => t + (x ?? 0), 0);
    return somme > 0 ? somme : null;
}
/** Le diagramme servi à l'élève : les éléments à donner VIDÉS — la réponse ne voyage pas. */
export function diagrammeAConstruire(d, aDonner) {
    const vides = new Set(aDonner);
    return avecElements(d, elementsDe(d).map((_, i) => (vides.has(i) ? null : undefined)));
}
/** Un nombre, écrit à la française : la virgule décimale, le signe moins typographique. */
export function ecrireNombre(n) {
    return fr(n);
}
/* ───────────────────────────────── le contrôle ───────────────────────────────── */
const LIBELLE_MAX = 22;
/**
 * Ce qu'un schéma ne voit pas d'un diagramme. `complet` : aucune valeur ne peut manquer (ce que l'auteur écrit).
 * `valeurs: false` ne garde que les griefs de STRUCTURE (catégories, bornes, graduation) : ceux qui empêchent
 * tout dessin. Une VALEUR indessinable — négative, hors de l'axe, des quartiles en désordre — est un grief pour
 * l'auteur, mais c'est aussi ce qu'un élève peut écrire : son diagramme se dessine quand même.
 */
export function griefsDeDiagramme(d, complet = true, valeurs = true) {
    const g = [];
    const nombres = (quoi, vs, positifs) => {
        if (!valeurs)
            return;
        vs.forEach((x, i) => {
            if (x === null) {
                if (complet)
                    g.push(`${quoi} ${i + 1} : valeur absente`);
            }
            else if (!Number.isFinite(x))
                g.push(`${quoi} ${i + 1} : pas un nombre`);
            else if (positifs && x < 0)
                g.push(`${quoi} ${i + 1} : une valeur négative ne se représente pas`);
        });
    };
    const libelles = (ls) => {
        ls.forEach((l, i) => {
            if (l.trim() === '')
                g.push(`catégorie ${i + 1} : nom vide`);
            else if ([...l].length > LIBELLE_MAX) {
                g.push(`catégorie ${i + 1} : « ${l} » dépasse ${LIBELLE_MAX} caractères — il ne tiendrait pas`);
            }
        });
        if (new Set(ls).size !== ls.length)
            g.push('deux catégories portent le même nom');
    };
    switch (d.type) {
        case 'bandes':
        case 'ligne_brisee':
        case 'circulaire': {
            if (d.categories.length !== d.valeurs.length) {
                g.push(`${d.categories.length} catégorie(s) pour ${d.valeurs.length} valeur(s)`);
            }
            if (d.categories.length < 2 || d.categories.length > 8)
                g.push('de 2 à 8 catégories');
            libelles(d.categories);
            nombres('valeur', d.valeurs, d.type !== 'ligne_brisee');
            if (d.type === 'circulaire') {
                const total = d.valeurs.reduce((s, x) => s + (x ?? 0), 0);
                if (valeurs && complet && !(total > 0))
                    g.push('la somme des valeurs est nulle : aucun secteur à dessiner');
            }
            break;
        }
        case 'histogramme': {
            if (d.bornes.length !== d.effectifs.length + 1) {
                g.push(`${d.effectifs.length} classe(s) demandent ${d.effectifs.length + 1} bornes ; ${d.bornes.length} écrite(s)`);
            }
            if (d.effectifs.length < 2 || d.effectifs.length > 10)
                g.push('de 2 à 10 classes');
            d.bornes.forEach((b, i) => {
                if (i > 0 && !(b > d.bornes[i - 1]))
                    g.push(`borne ${i + 1} : les bornes sont croissantes`);
            });
            const largeurs = d.bornes.slice(1).map((b, i) => b - d.bornes[i]);
            if (largeurs.some((l) => Math.abs(l - (largeurs[0] ?? 0)) > 1e-9)) {
                g.push('les classes ont la même largeur (un histogramme à classes inégales n’est pas dessiné)');
            }
            nombres('effectif', d.effectifs, true);
            break;
        }
        case 'quartiles': {
            const cinq = CLES_QUARTILES.map((c) => d[c]);
            nombres('nombre', cinq, false);
            const connus = cinq.filter((x) => x !== null);
            if (valeurs && connus.some((x, i) => i > 0 && x < connus[i - 1])) {
                g.push('les cinq nombres sont croissants : minimum, premier quartile, médiane, troisième quartile, maximum');
            }
            if (d.graduation !== undefined) {
                const { de, a, pas } = d.graduation;
                if (!(a > de) || !(pas > 0) || (a - de) / pas > 40) {
                    g.push('graduation : « a » dépasse « de », le pas est positif, 40 graduations au plus');
                }
                else if (valeurs && connus.some((x) => x < de || x > a)) {
                    g.push('graduation : un des cinq nombres est hors de l’axe');
                }
            }
            /* PLUSIEURS SÉRIES sur le même axe (tranche T36). */
            const autres = d.autres ?? [];
            if (d.autres !== undefined || d.nom !== undefined) {
                if (autres.length < 1 || autres.length > 3)
                    g.push('autres : de 1 à 3 séries en plus de la première');
                if (d.nom === undefined)
                    g.push('nom : la première série se nomme dès qu’il y en a d’autres');
                /* « À lire seulement » se contrôle à l'import (`valider-exercices.ts`) : ici on ne
                   sait pas si le diagramme est une réponse à compléter. */
                const noms = [d.nom ?? '', ...autres.map((a) => a.nom)];
                if (noms.some((n) => n.trim().length < 1 || n.trim().length > NOM_DE_SERIE_MAX)) {
                    g.push(`le nom d’une série a de 1 à ${NOM_DE_SERIE_MAX} caractères`);
                }
                if (new Set(noms.map((n) => n.trim())).size !== noms.length) {
                    g.push('deux séries portent le même nom');
                }
                autres.forEach((a) => {
                    const v = CLES_QUARTILES.map((c) => a[c]);
                    if (v.some((x) => !Number.isFinite(x))) {
                        g.push(`série « ${a.nom} » : un des cinq nombres n’est pas un nombre`);
                    }
                    else if (v.some((x, i) => i > 0 && x < v[i - 1])) {
                        g.push(`série « ${a.nom} » : les cinq nombres sont croissants`);
                    }
                    else if (d.graduation !== undefined &&
                        v.some((x) => x < d.graduation.de || x > d.graduation.a)) {
                        g.push(`série « ${a.nom} » : un des cinq nombres est hors de l’axe`);
                    }
                });
            }
            break;
        }
        case 'nuage': {
            if (d.points.length < 2 || d.points.length > 30)
                g.push('de 2 à 30 points');
            nombres('ordonnée', d.points.map((p) => p.y), false);
            if (d.points.some((p) => !Number.isFinite(p.x)))
                g.push('une abscisse n’est pas un nombre');
            break;
        }
        case 'arbre': {
            const toutes = branchesDe(d.branches);
            const feuilles = toutes.filter(({ b }) => estFeuille(b));
            if (toutes.some(({ chemin }) => chemin.length > 3))
                g.push('un arbre a trois étapes au plus');
            if (feuilles.length < 2 || feuilles.length > 12)
                g.push('de 2 à 12 issues au bout de l’arbre');
            if (d.etapes !== undefined && d.etapes.some((e) => [...e].length > 16)) {
                g.push('le nom d’une étape a 16 caractères au plus');
            }
            const noeuds = [
                d.branches,
                ...toutes.filter(({ b }) => !estFeuille(b)).map(({ b }) => b.branches ?? []),
            ];
            for (const enfants of noeuds) {
                if (enfants.length < 2)
                    g.push('un nœud a au moins deux branches');
                if (new Set(enfants.map((b) => b.issue)).size !== enfants.length) {
                    g.push('deux branches d’un même nœud mènent à la même issue');
                }
            }
            for (const { b } of toutes) {
                if (b.issue.trim() === '' || [...b.issue].length > 12) {
                    g.push(`issue « ${b.issue} » : de 1 à 12 caractères`);
                }
                if (!valeurs)
                    continue;
                if (b.p === null) {
                    if (complet)
                        g.push(`branche « ${b.issue} » : probabilité absente`);
                    continue;
                }
                const r = lireRationnel(b.p);
                if (r === null)
                    g.push(`branche « ${b.issue} » : « ${b.p} » n’est pas une probabilité`);
                else if (r.n < 0 || r.n > r.d) {
                    g.push(`branche « ${b.issue} » : une probabilité est comprise entre 0 et 1`);
                }
            }
            /* À CHAQUE NŒUD, les probabilités somment à 1 — exactement. */
            if (valeurs) {
                for (const enfants of noeuds) {
                    const lues = enfants.map((b) => (b.p === null ? null : lireRationnel(b.p)));
                    if (lues.some((r) => r === null))
                        continue;
                    let n = 0;
                    let de = 1;
                    for (const r of lues) {
                        n = n * r.d + r.n * de;
                        de *= r.d;
                        const k = pgcd(n, de) || 1;
                        n /= k;
                        de /= k;
                    }
                    if (n !== de) {
                        g.push(`les probabilités des branches ${enfants.map((b) => `« ${b.issue} »`).join(', ')} somment à ${ecrireFraction({ n, d: de })}, pas à 1`);
                    }
                }
            }
            break;
        }
        case 'venn': {
            if (d.ensembles.length !== 2 && d.ensembles.length !== 3) {
                g.push('un diagramme de Venn a deux ou trois ensembles');
                break;
            }
            d.ensembles.forEach((e, i) => {
                if (e.trim() === '' || [...e].length > 18)
                    g.push(`ensemble ${i + 1} : un nom de 1 à 18 caractères`);
            });
            if (new Set(d.ensembles).size !== d.ensembles.length)
                g.push('deux ensembles portent le même nom');
            const attendues = regionsDe(d.ensembles);
            const ecrites = Object.keys(d.regions);
            if (ecrites.length !== attendues.length || attendues.some((r) => !(r in d.regions))) {
                g.push(`les régions s’écrivent toutes, sous ces noms : ${attendues.join(', ')}`);
            }
            nombres('région', attendues.map((r) => d.regions[r] ?? null), true);
            break;
        }
        case 'graphe': {
            const noms = d.sommets.map((s) => s.nom);
            if (noms.length < 2 || noms.length > 10)
                g.push('de 2 à 10 sommets');
            if (new Set(noms).size !== noms.length)
                g.push('deux sommets portent le même nom');
            if (noms.some((n) => n.trim() === '' || [...n].length > 6)) {
                g.push('le nom d’un sommet a de 1 à 6 caractères');
            }
            const places = d.sommets.filter((s) => s.x !== undefined && s.y !== undefined).length;
            if (places !== 0 && places !== d.sommets.length) {
                g.push('ou tous les sommets sont placés (x, y), ou aucun');
            }
            if (d.sommets.some((s) => [s.x, s.y].some((c) => c !== undefined && (c < 0 || c > 100)))) {
                g.push('la place d’un sommet : x et y de 0 à 100');
            }
            /* Un graphe d'AUTEUR a au moins une arête ; un graphe À CONSTRUIRE (L5, F9) est
               servi sans les arêtes à tracer — parfois sans aucune. */
            if ((complet && d.aretes.length < 1) || d.aretes.length > 20)
                g.push('de 1 à 20 arêtes');
            const vues = new Set();
            d.aretes.forEach((a, i) => {
                if (!noms.includes(a.de) || !noms.includes(a.a)) {
                    g.push(`arête ${i + 1} : « ${a.de} » – « ${a.a} » relie des sommets qui existent`);
                }
                else if (a.de === a.a)
                    g.push(`arête ${i + 1} : une arête relie deux sommets différents`);
                const k = a.oriente === true ? `${a.de}>${a.a}` : [a.de, a.a].sort().join('|');
                if (vues.has(k))
                    g.push(`arête ${i + 1} : « ${a.de} » – « ${a.a} » est écrite deux fois`);
                vues.add(k);
                if (a.poids !== undefined && !Number.isFinite(a.poids))
                    g.push(`arête ${i + 1} : poids`);
            });
            break;
        }
    }
    return g;
}
/** Au-delà, une échelle n'a plus de sens à l'écran. */
const DEMESURE = 1e6;
/**
 * Le diagramme TEL QU'IL PEUT SE DESSINER. Une valeur qu'on ne sait pas représenter — négative là où l'on
 * compte, démesurée, hors de l'axe gradué — est retirée du DESSIN (elle y redevient un creux) ; elle n'est
 * jamais corrigée, ni déplacée. C'est ce qui permet de dessiner ce qu'un élève a écrit, juste ou non : la
 * liste de ses éléments, à côté, porte la valeur exacte. Un diagramme d'auteur, déjà contrôlé, en sort inchangé.
 */
export function diagrammeDessinable(d) {
    const garde = (positif, de = -DEMESURE, a = DEMESURE) => {
        return (x) => x === null || !Number.isFinite(x) || x < Math.max(de, positif ? 0 : de) || x > a ? null : x;
    };
    switch (d.type) {
        case 'bandes':
        case 'circulaire':
            return { ...d, valeurs: d.valeurs.map(garde(true)) };
        case 'ligne_brisee':
            return { ...d, valeurs: d.valeurs.map(garde(false)) };
        case 'histogramme':
            return { ...d, effectifs: d.effectifs.map(garde(true)) };
        case 'quartiles': {
            const g = d.graduation === undefined ? garde(false) : garde(false, d.graduation.de, d.graduation.a);
            return {
                ...d,
                min: g(d.min),
                q1: g(d.q1),
                mediane: g(d.mediane),
                q3: g(d.q3),
                max: g(d.max),
            };
        }
        case 'nuage':
            return { ...d, points: d.points.map((p) => ({ x: p.x, y: garde(false)(p.y) })) };
        case 'venn':
            return {
                ...d,
                regions: Object.fromEntries(Object.entries(d.regions).map(([cle, x]) => [cle, garde(true)(x)])),
            };
        case 'arbre':
        case 'graphe':
            return d;
    }
}
/* ───────────────────────────────── le dessin ───────────────────────────────── */
const L = LARGEUR_TOILE;
const GAUCHE = 76;
const DROITE = 28;
const BAS = 78;
const CORPS = 15;
const SURFACES_SECTEUR = [
    'teinte',
    'papier',
    'teinte_forte',
    'ardoise',
    'jeton_accent',
    'paquet',
    'jeton_papier',
    'jeton_efface',
];
/** Le secteur d'une part À TROUVER : neutre, jamais la couleur d'une catégorie voisine. */
const SURFACE_A_TROUVER = 'jeton_efface';
/** La place, à gauche de l'axe, du nom d'une série d'un diagramme de quartiles à plusieurs séries. */
const LARGEUR_NOM_DE_SERIE = 118;
/** La longueur d'un nom de série : il tient dans cette place. */
const NOM_DE_SERIE_MAX = 14;
/** Le haut de l'axe des EFFECTIFS quand aucune valeur n'est connue ni aucune étendue posée : gradué de 1 en 1. */
const ECHELLE_SANS_VALEUR = 5;
/** Une échelle « ronde » de `de` à au moins `a` : le pas (1, 2 ou 5 × 10ⁿ) et la borne haute. */
export function echelleRonde(de, a, graduations = 6) {
    const etendue = Math.max(a - de, 1e-9);
    const brut = etendue / graduations;
    const puissance = 10 ** Math.floor(Math.log10(brut));
    const f = brut / puissance;
    const pas = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * puissance;
    const bas = Math.floor(de / pas + 1e-9) * pas;
    const haut = Math.ceil(a / pas - 1e-9) * pas;
    return { pas, bas, haut: haut === bas ? bas + pas : haut };
}
const n1 = (v) => (Math.round(v * 10) / 10).toString();
const pt = (x, y) => `${n1(x)} ${n1(y)}`;
function texte(x, y, t, ancre = 'milieu', fort = false, taille = CORPS) {
    return {
        type: 'texte',
        x,
        y,
        texte: t,
        taille,
        ancre,
        police: fort ? 'etiquette_forte' : 'etiquette',
        ton: fort ? 'encre' : 'doux',
    };
}
const trait = (x1, y1, x2, y2, quadrille = false) => ({
    type: 'chemin',
    d: `M${pt(x1, y1)} L${pt(x2, y2)}`,
    trait: quadrille ? 'quadrille' : 'encre',
});
/** Les axes d'un diagramme à deux axes, l'échelle verticale graduée, les noms des axes. */
function cadre(traces, haut, hauteur, axeX, axeY, ymin, ymax, servi) {
    const bas = hauteur - BAS;
    /* L'axe SERVI (celui du diagramme complet, `axeValeurs`) est gardé tel quel tant que
       tout y tient ; une valeur de l'élève qui en sort fait regraduer l'axe autour d'elle. */
    const tient = servi !== undefined && ymin >= servi.de - 1e-9 && ymax <= servi.a + 1e-9;
    const { pas, bas: y0, haut: y1, } = tient
        ? { pas: servi.pas, bas: servi.de, haut: servi.a }
        : echelleRonde(Math.min(0, ymin, servi?.de ?? 0), Math.max(ymax, servi?.a ?? ymax, ymin + 1e-9));
    const y = (v) => bas - ((v - y0) / (y1 - y0)) * (bas - haut);
    for (let v = y0; v <= y1 + pas / 1e6; v += pas) {
        const arrondi = Math.round(v / pas) * pas;
        traces.push(trait(GAUCHE, y(arrondi), L - DROITE, y(arrondi), true));
        traces.push(texte(GAUCHE - 8, y(arrondi) + 5, fr(arrondi), 'fin'));
    }
    traces.push(trait(GAUCHE, haut, GAUCHE, bas), trait(GAUCHE, y(0), L - DROITE, y(0)));
    traces.push(texte(GAUCHE - 60, haut - 14, axeY, 'debut', true, 14));
    traces.push(texte((GAUCHE + L - DROITE) / 2, hauteur - 14, axeX, 'milieu', true, 14));
    return { y, bas, axe: { de: y0, a: y1, pas, debut: bas, fin: haut } };
}
/**
 * Résout un diagramme en traces. Ne lève jamais : ce qui ne peut pas se
 * dessiner (des données bancales) est dit dans `griefs`, et le reste est rendu.
 */
export function resoudreDiagramme(brut, decrit) {
    return resoudreAvecGeometrie(brut, decrit).resolu;
}
/**
 * LA GÉOMÉTRIE D'UN DIAGRAMME DESSINÉ (atelier de figures, L5, F7 et F9) : où
 * tombent ses valeurs, ses colonnes, ses points, ses sommets, en unités de
 * toile. C'est elle qui permet de MANIPULER un diagramme — glisser une barre,
 * poser un point, toucher une arête — sur le dessin même que `resoudreDiagramme`
 * rend, sans un second calcul d'échelle.
 */
export function geometrieDuDiagramme(d) {
    return resoudreAvecGeometrie(d, '').geometrie;
}
function resoudreAvecGeometrie(brut, decrit) {
    /* Les griefs sont RENDUS en entier (l'import refuse sur eux) ; seuls ceux de structure empêchent le dessin. */
    const griefs = [...griefsDeDiagramme(brut, false)];
    const d = diagrammeDessinable(brut);
    const traces = [];
    const haut = d.titre === undefined ? 34 : 64;
    if (d.titre !== undefined)
        traces.push(texte(L / 2, 26, d.titre, 'milieu', true, 18));
    let geometrie = { genre: 'aucune', largeur: L, hauteur: 120 };
    const rendu = (hauteur) => ({
        resolu: { largeur: L, hauteur, decrit, traces, griefs },
        geometrie: { ...geometrie, largeur: L, hauteur },
    });
    if (griefsDeDiagramme(brut, false, false).length > 0)
        return rendu(120);
    switch (d.type) {
        case 'bandes':
        case 'ligne_brisee': {
            const H = 380;
            const connues = d.valeurs.filter((x) => x !== null);
            /* L'axe couvre l'étendue du diagramme COMPLET quand elle est connue (`axeValeurs`),
               et ce que l'élève écrit au-delà. Sans rien de connu, des bandes comptent des
               effectifs : une échelle ENTIÈRE, pas des dixièmes. */
            const { y, bas, axe } = cadre(traces, haut, H, d.axeX, d.axeY, Math.min(0, ...connues), Math.max(...connues, d.axeValeurs !== undefined
                ? d.axeValeurs.de
                : connues.length === 0 && d.type === 'bandes'
                    ? ECHELLE_SANS_VALEUR
                    : 1), d.axeValeurs);
            const pas = (L - DROITE - GAUCHE) / d.categories.length;
            geometrie = {
                genre: 'valeurs',
                largeur: L,
                hauteur: H,
                axe,
                colonnes: d.categories.map((_, i) => ({
                    rang: i,
                    x: GAUCHE + pas * (i + 0.5) - (pas * 0.6) / 2,
                    largeur: pas * 0.6,
                })),
            };
            const points = [];
            d.categories.forEach((nom, i) => {
                const cx = GAUCHE + pas * (i + 0.5);
                const v = d.valeurs[i] ?? null;
                traces.push(texte(cx, bas + 20, nom, 'milieu', false, d.categories.length > 6 ? 12 : 14));
                if (d.type === 'bandes') {
                    const l = pas * 0.6;
                    if (v === null) {
                        /* Pas encore donnée : un emplacement en creux, jamais une hauteur inventée. */
                        traces.push({
                            type: 'rect',
                            x: cx - l / 2,
                            y: y(0) - 26,
                            largeur: l,
                            hauteur: 26,
                            rayon: 2,
                            surface: 'jeton_efface',
                        });
                        traces.push(texte(cx, y(0) - 8, '?', 'milieu', true));
                    }
                    else {
                        const sommet = Math.min(y(v), y(0));
                        traces.push({
                            type: 'rect',
                            x: cx - l / 2,
                            y: sommet,
                            largeur: l,
                            hauteur: Math.abs(y(v) - y(0)),
                            rayon: 0,
                            surface: 'teinte',
                        });
                        if (d.etiquettes === true)
                            traces.push(texte(cx, sommet - 6, fr(v), 'milieu', true, 13));
                    }
                }
                else if (v === null) {
                    traces.push(texte(cx, y(0) - 8, '?', 'milieu', true));
                }
                else {
                    points.push([cx, y(v)]);
                    if (d.etiquettes === true)
                        traces.push(texte(cx, y(v) - 10, fr(v), 'milieu', true, 13));
                }
            });
            if (points.length > 1) {
                traces.push({
                    type: 'chemin',
                    d: points.map(([x, yy], i) => `${i === 0 ? 'M' : 'L'}${pt(x, yy)}`).join(' '),
                    trait: 'encre',
                });
            }
            for (const [x, yy] of points)
                traces.push({ type: 'cercle', cx: x, cy: yy, r: 5, ton: 'accent' });
            return rendu(H);
        }
        case 'histogramme': {
            const H = 380;
            const connus = d.effectifs.filter((x) => x !== null);
            const { y, bas, axe } = cadre(traces, haut, H, d.axeX, d.axeY, 0, Math.max(...connus, d.axeValeurs !== undefined ? 0 : connus.length === 0 ? ECHELLE_SANS_VALEUR : 1), d.axeValeurs);
            const x0 = d.bornes[0];
            const x1 = d.bornes[d.bornes.length - 1];
            const x = (v) => GAUCHE + 12 + ((v - x0) / (x1 - x0)) * (L - DROITE - GAUCHE - 24);
            geometrie = {
                genre: 'valeurs',
                largeur: L,
                hauteur: H,
                axe,
                colonnes: d.effectifs.map((_, i) => ({
                    rang: i,
                    x: x(d.bornes[i]),
                    largeur: x(d.bornes[i + 1]) - x(d.bornes[i]),
                })),
            };
            d.bornes.forEach((b) => {
                traces.push(trait(x(b), bas, x(b), bas + 6));
                traces.push(texte(x(b), bas + 22, fr(b), 'milieu', false, d.bornes.length > 8 ? 12 : 14));
            });
            d.effectifs.forEach((v, i) => {
                const g = x(d.bornes[i]);
                const l = x(d.bornes[i + 1]) - g;
                if (v === null) {
                    traces.push({
                        type: 'rect',
                        x: g,
                        y: y(0) - 26,
                        largeur: l,
                        hauteur: 26,
                        rayon: 0,
                        surface: 'jeton_efface',
                    });
                    traces.push(texte(g + l / 2, y(0) - 8, '?', 'milieu', true));
                }
                else {
                    traces.push({
                        type: 'rect',
                        x: g,
                        y: y(v),
                        largeur: l,
                        hauteur: y(0) - y(v),
                        rayon: 0,
                        surface: 'teinte',
                    });
                    if (d.etiquettes === true)
                        traces.push(texte(g + l / 2, y(v) - 6, fr(v), 'milieu', true, 13));
                }
            });
            return rendu(H);
        }
        case 'circulaire': {
            const lignes = d.categories.length;
            const H = Math.max(300, haut + 40 + lignes * 30);
            const cx = 190;
            const cy = haut + (H - haut) / 2 - 8;
            const r = Math.min(130, (H - haut) / 2 - 24);
            /* À QUOI LES PARTS SE RAPPORTENT. Au total du diagramme complet quand il
               est connu (`total`, posé par `avecElements`) ; sinon à leur somme, si
               aucune ne manque. Une part qui manque sans total connu : RIEN ne se
               rapporte à rien — ni secteur, ni pourcentage. Jamais la somme des
               seules parts présentes : elle écrirait des pourcentages faux. */
            const somme = d.valeurs.reduce((s, v) => s + (v ?? 0), 0);
            const manquantes = d.valeurs.filter((v) => v === null).length;
            const reference = d.total ?? (manquantes === 0 && somme > 0 ? somme : null);
            /* Le tour du disque : le total, ou ce que l'élève a écrit s'il le dépasse. */
            const tour = reference === null ? null : Math.max(reference, somme);
            /* Ce qui reste du disque — les parts à trouver, ou ce qui manque au total. */
            const reste = tour === null ? 1 : Math.max(0, 1 - somme / tour);
            let depart = -Math.PI / 2;
            const secteur = (part, surface, ecrit) => {
                if (part <= 1e-9)
                    return;
                const angle = part * 2 * Math.PI;
                const pas = Math.max(2, Math.ceil(angle / (Math.PI / 36)));
                const points = part >= 1 - 1e-9 ? [] : [[cx, cy]];
                for (let k = 0; k <= pas; k += 1) {
                    const a = depart + (angle * k) / pas;
                    points.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
                }
                traces.push({ type: 'polygone', points, surface });
                /* Le NUMÉRO du secteur, dedans : la couleur seule ne dit rien. */
                const milieu = depart + angle / 2;
                const d0 = part >= 1 - 1e-9 ? 0 : r * 0.62;
                if (ecrit !== '')
                    traces.push(texte(cx + d0 * Math.cos(milieu), cy + d0 * Math.sin(milieu) + 5, ecrit, 'milieu', true, 14));
                depart += angle;
            };
            d.categories.forEach((nom, i) => {
                const v = d.valeurs[i] ?? null;
                const surface = v === null
                    ? SURFACE_A_TROUVER
                    : SURFACES_SECTEUR[i % SURFACES_SECTEUR.length];
                const ly = haut + 34 + i * 30;
                const suffixe = v === null
                    ? ' — ?'
                    : d.afficher === 'rien'
                        ? ''
                        : d.afficher === 'valeur'
                            ? ` — ${fr(v)}`
                            : reference === null
                                ? ''
                                : ` — ${fr(Math.round((v / reference) * 1000) / 10)} %`;
                traces.push({
                    type: 'rect',
                    x: 380,
                    y: ly - 15,
                    largeur: 20,
                    hauteur: 20,
                    rayon: 3,
                    surface,
                });
                traces.push(texte(390, ly, String(i + 1), 'milieu', true, 12));
                traces.push(texte(410, ly, `${nom}${suffixe}`, 'debut', false, 15));
                if (v === null) {
                    /* UNE seule part à trouver : son secteur est à sa place, neutre, et
                       porte son numéro et « ? ». Plusieurs : leur partage n'est pas connu,
                       elles se dessinent ensemble, après les autres. */
                    if (manquantes === 1 && tour !== null)
                        secteur(reste, SURFACE_A_TROUVER, `${i + 1} ?`);
                    return;
                }
                if (tour !== null)
                    secteur(v / tour, surface, String(i + 1));
            });
            if (tour === null) {
                /* Sans total : le disque entier est à trouver. */
                secteur(1, SURFACE_A_TROUVER, '?');
            }
            else if (manquantes !== 1) {
                /* Plusieurs parts à trouver, ou des parts qui n'atteignent pas le total. */
                secteur(reste, SURFACE_A_TROUVER, manquantes > 0 ? '?' : '');
            }
            return rendu(H);
        }
        case 'quartiles': {
            /* Une série, ou PLUSIEURS sur le même axe (tranche T36) : l'une sous l'autre,
               chacune avec son nom à gauche, pour comparer deux groupes d'un coup d'œil. */
            const autres = d.autres ?? [];
            const plusieurs = autres.length > 0;
            const boiteH = plusieurs ? 42 : 54;
            const ENTRE = 20;
            const H = (d.titre === undefined ? 190 : 220) +
                (plusieurs ? autres.length * (boiteH + ENTRE) - 12 : 0);
            const cinq = CLES_QUARTILES.map((c) => d[c]);
            const connus = cinq.filter((v) => v !== null);
            const tous = [...connus, ...autres.flatMap((a) => CLES_QUARTILES.map((c) => a[c]))];
            const g = d.graduation ??
                (() => {
                    const e = echelleRonde(Math.min(...tous, 0 + (tous[0] ?? 0)), Math.max(...tous, (tous[0] ?? 0) + 1), 8);
                    return { de: e.bas, a: e.haut, pas: e.pas };
                })();
            /* Le nom des séries prend sa place à gauche de l'axe. */
            const x0 = plusieurs ? 44 + LARGEUR_NOM_DE_SERIE : 44;
            const x = (v) => x0 + ((v - g.de) / (g.a - g.de)) * (L - 44 - x0);
            const axeY = H - 62;
            const cyB = haut + 6 + boiteH / 2;
            traces.push(trait(x(g.de), axeY, x(g.a), axeY));
            const pasEcrit = (g.a - g.de) / g.pas > 16 ? 2 : 1;
            for (let k = 0; g.de + k * g.pas <= g.a + g.pas / 1e6; k += 1) {
                const v = g.de + k * g.pas;
                traces.push(trait(x(v), axeY, x(v), axeY + 7));
                if (k % pasEcrit === 0)
                    traces.push(texte(x(v), axeY + 24, fr(v), 'milieu', false, 13));
            }
            traces.push(texte((x0 + L - 44) / 2, H - 10, d.axe, 'milieu', true, 14));
            /* EN CONSTRUCTION (un des cinq nombres manque) : chaque nombre déjà placé porte sa valeur, au-dessus de son
               trait. Sans elle, l'élève voyait des traits nus, sans pouvoir dire lequel est la médiane (vu en recette
               navigateur, T12). Un diagramme complet, lui, se LIT sur l'axe : rien n'y est ajouté. */
            if (cinq.some((v) => v === null)) {
                CLES_QUARTILES.forEach((cle, i) => {
                    const v = d[cle];
                    if (v === null)
                        return;
                    const court = ['min.', 'Q1', 'méd.', 'Q3', 'max.'][i];
                    traces.push(texte(x(v), cyB - boiteH / 2 - 7, `${court} ${fr(v)}`, 'milieu', false, 12));
                });
            }
            /** Une boîte et ses moustaches, centrées en `cy`. */
            const boite = (serie, cy) => {
                const { min: mn, q1, mediane: me, q3, max: mx } = serie;
                const vus = CLES_QUARTILES.map((c) => serie[c]).filter((v) => v !== null);
                /* Cinq nombres en DÉSORDRE (un élève a écrit un quartile au-delà de la médiane) : ni boîte ni
                   moustaches, qui mentiraient — chaque nombre à sa place, d'un trait. */
                if (vus.some((v, i) => i > 0 && v < vus[i - 1])) {
                    for (const v of vus)
                        traces.push(trait(x(v), cy - boiteH / 2, x(v), cy + boiteH / 2));
                    return;
                }
                if (q1 !== null && q3 !== null) {
                    traces.push({
                        type: 'rect',
                        x: x(q1),
                        y: cy - boiteH / 2,
                        largeur: Math.max(1, x(q3) - x(q1)),
                        hauteur: boiteH,
                        rayon: 0,
                        surface: 'teinte',
                    });
                }
                if (me !== null)
                    traces.push(trait(x(me), cy - boiteH / 2, x(me), cy + boiteH / 2));
                if (mn !== null) {
                    traces.push(trait(x(mn), cy - 14, x(mn), cy + 14));
                    if (q1 !== null)
                        traces.push(trait(x(mn), cy, x(q1), cy));
                }
                if (mx !== null) {
                    traces.push(trait(x(mx), cy - 14, x(mx), cy + 14));
                    if (q3 !== null)
                        traces.push(trait(x(q3), cy, x(mx), cy));
                }
                /* Un quartile isolé (sa boîte n'est pas encore fermée) : son trait seul. */
                if (q1 !== null && q3 === null)
                    traces.push(trait(x(q1), cy - boiteH / 2, x(q1), cy + boiteH / 2));
                if (q3 !== null && q1 === null)
                    traces.push(trait(x(q3), cy - boiteH / 2, x(q3), cy + boiteH / 2));
            };
            boite(d, cyB);
            if (plusieurs) {
                traces.push(texte(x0 - 14, cyB + 5, d.nom ?? '', 'fin', true, 14));
                autres.forEach((a, i) => {
                    const cy = cyB + (i + 1) * (boiteH + ENTRE);
                    boite(a, cy);
                    traces.push(texte(x0 - 14, cy + 5, a.nom, 'fin', true, 14));
                });
            }
            return rendu(H);
        }
        case 'nuage': {
            const H = 400;
            const ys = d.points.map((p) => p.y).filter((v) => v !== null);
            const xs = d.points.map((p) => p.x);
            const { y, bas, axe } = cadre(traces, haut, H, d.axeX, d.axeY, Math.min(0, ...ys), Math.max(...ys, d.axeValeurs !== undefined ? d.axeValeurs.de : 1), d.axeValeurs);
            const ex = echelleRonde(Math.min(0, ...xs), Math.max(...xs, 1), 8);
            const x = (v) => GAUCHE + ((v - ex.bas) / (ex.haut - ex.bas)) * (L - DROITE - GAUCHE);
            geometrie = {
                genre: 'nuage',
                largeur: L,
                hauteur: H,
                axe,
                axeX: { de: ex.bas, a: ex.haut, pas: ex.pas, debut: GAUCHE, fin: L - DROITE },
            };
            for (let v = ex.bas; v <= ex.haut + ex.pas / 1e6; v += ex.pas) {
                const arrondi = Math.round(v / ex.pas) * ex.pas;
                traces.push(trait(x(arrondi), bas, x(arrondi), bas + 6));
                traces.push(texte(x(arrondi), bas + 22, fr(arrondi), 'milieu', false, 13));
            }
            if (d.droite !== undefined) {
                const f = (v) => d.droite.a * v + d.droite.b;
                traces.push({
                    type: 'chemin',
                    d: `M${pt(x(ex.bas), y(f(ex.bas)))} L${pt(x(ex.haut), y(f(ex.haut)))}`,
                    trait: 'doux',
                });
            }
            for (const p of d.points) {
                if (p.y === null)
                    traces.push(texte(x(p.x), bas - 8, '?', 'milieu', true));
                else
                    traces.push({ type: 'cercle', cx: x(p.x), cy: y(p.y), r: 5, ton: 'accent' });
            }
            return rendu(H);
        }
        case 'arbre': {
            /* De la racine, à gauche, aux issues, à droite ; une colonne par étape. */
            const toutes = branchesDe(d.branches);
            const feuilles = toutes.filter(({ b }) => estFeuille(b));
            const etapes = Math.max(...toutes.map(({ chemin }) => chemin.length));
            const pas = Math.max(34, Math.min(58, 300 / feuilles.length));
            const sommet = haut + (d.etapes === undefined ? 4 : 30);
            const H = sommet + feuilles.length * pas + 14;
            const X0 = 46;
            const bout = d.issues === true || d.produits === true ? 210 : 70;
            const colonne = (L - X0 - bout) / etapes;
            const largeurDe = (t) => [...t].length * 9 + 10;
            (d.etapes ?? []).slice(0, etapes).forEach((nom, k) => {
                traces.push(texte(X0 + (k + 0.5) * colonne, haut + 12, nom, 'milieu', false, 13));
            });
            /* La hauteur de chaque nœud : une issue par ligne ; un nœud au milieu de ses suites. */
            let ligne = 0;
            const y = new Map();
            const poser = (branches) => {
                const ys = branches.map((b) => {
                    const yb = estFeuille(b) ? sommet + (ligne++ + 0.5) * pas : poser(b.branches ?? []);
                    y.set(b, yb);
                    return yb;
                });
                return ((ys[0] ?? 0) + (ys[ys.length - 1] ?? 0)) / 2;
            };
            const yRacine = poser(d.branches);
            traces.push({ type: 'cercle', cx: X0, cy: yRacine, r: 4, ton: 'accent' });
            for (const { b, chemin } of toutes) {
                const k = chemin.length;
                const parent = chemin[k - 2];
                const xa = parent === undefined ? X0 + 6 : X0 + (k - 1) * colonne + largeurDe(parent.issue);
                const ya = parent === undefined ? yRacine : y.get(parent);
                const xb = X0 + k * colonne;
                const yb = y.get(b);
                traces.push(trait(xa, ya, xb - 8, yb));
                /* La probabilité, du côté LIBRE de sa branche — au-dessus d'une branche qui monte, en
                   dessous d'une branche qui descend — ; « ? » tant qu'elle n'est pas donnée. */
                const xm = xa + (xb - 8 - xa) * 0.55;
                const ym = ya + (yb - ya) * 0.55 + (yb > ya + 1 ? 17 : -7);
                traces.push(b.p === null
                    ? texte(xm, ym, '?', 'milieu', true, 16)
                    : texte(xm, ym, ecrireValeur(b.p), 'milieu', false, 14));
                traces.push(texte(xb, yb + 5, b.issue, 'debut', true, 15));
                if (estFeuille(b) && (d.issues === true || d.produits === true)) {
                    const issue = d.issues === true ? `(${chemin.map((c) => c.issue).join(', ')})` : '';
                    const valeur = valeurDIssue(chemin);
                    const produit = d.produits === true
                        ? `${issue === '' ? '' : ' : '}${valeur === null ? '?' : ecrireValeur(valeur)}`
                        : '';
                    traces.push(texte(xb + largeurDe(b.issue) + 14, yb + 5, `${issue}${produit}`, 'debut', false, 14));
                }
            }
            return rendu(H);
        }
        case 'venn': {
            const trois = d.ensembles.length === 3;
            const H = haut + (trois ? 356 : 292);
            const cx = L / 2;
            const r = trois ? 92 : 104;
            const cy = haut + (trois ? 132 : 150);
            traces.push({
                type: 'rect',
                x: 60,
                y: haut + 4,
                largeur: L - 120,
                hauteur: H - haut - 14,
                rayon: 10,
                surface: 'papier',
            });
            if (d.univers !== undefined)
                traces.push(texte(76, haut + 26, d.univers, 'debut', false, 13));
            const centres = trois
                ? [
                    [cx - 52, cy],
                    [cx + 52, cy],
                    [cx, cy + 88],
                ]
                : [
                    [cx - 60, cy],
                    [cx + 60, cy],
                ];
            for (const [x0, y0] of centres) {
                const points = Array.from({ length: 73 }, (_, i) => {
                    const t = (i / 72) * 2 * Math.PI;
                    return `${i === 0 ? 'M' : 'L'}${pt(x0 + r * Math.cos(t), y0 + r * Math.sin(t))}`;
                });
                traces.push({ type: 'chemin', d: points.join(' '), trait: 'encre' });
            }
            /* Le nom de chaque ensemble, hors de son cercle. */
            const [nomA, nomB, nomC] = d.ensembles;
            traces.push(texte(cx - 60 - r * 0.72, cy - r * 0.78, nomA, 'fin', true, 14));
            traces.push(texte(cx + 60 + r * 0.72, cy - r * 0.78, nomB, 'debut', true, 14));
            if (nomC !== undefined)
                traces.push(texte(cx + r * 0.78, cy + 88 + r * 0.86, nomC, 'debut', true, 14));
            /* La valeur de chaque région, en son milieu ; « ? » tant qu'elle n'est pas donnée. */
            const places = trois
                ? {
                    A: [cx - 92, cy - 22],
                    B: [cx + 92, cy - 22],
                    C: [cx, cy + 132],
                    AB: [cx, cy - 44],
                    AC: [cx - 58, cy + 62],
                    BC: [cx + 58, cy + 62],
                    ABC: [cx, cy + 30],
                    dehors: [L - 96, H - 28],
                }
                : {
                    A: [cx - 104, cy],
                    B: [cx + 104, cy],
                    AB: [cx, cy],
                    dehors: [L - 96, H - 28],
                };
            for (const cle of regionsDe(d.ensembles)) {
                const [x0, y0] = places[cle];
                const v = d.regions[cle] ?? null;
                traces.push(v === null
                    ? texte(x0, y0 + 6, '?', 'milieu', true, 18)
                    : texte(x0, y0 + 6, fr(v), 'milieu', true, 16));
            }
            return rendu(H);
        }
        case 'graphe': {
            const H = haut + 300;
            const R = 17;
            const n = d.sommets.length;
            const place = new Map(d.sommets.map((s, i) => {
                if (s.x !== undefined && s.y !== undefined) {
                    return [s.nom, [90 + (s.x / 100) * (L - 180), haut + 30 + ((100 - s.y) / 100) * 240]];
                }
                /* Sans place écrite : en cercle, le premier en haut, dans le sens des aiguilles. */
                const t = -Math.PI / 2 + (i / n) * 2 * Math.PI;
                return [s.nom, [L / 2 + 190 * Math.cos(t), haut + 150 + 118 * Math.sin(t)]];
            }));
            geometrie = { genre: 'graphe', largeur: L, hauteur: H, places: place, rayon: R };
            for (const a of d.aretes) {
                const [x1, y1] = place.get(a.de);
                const [x2, y2] = place.get(a.a);
                const l = Math.max(Math.hypot(x2 - x1, y2 - y1), 1e-9);
                const [ux, uy] = [(x2 - x1) / l, (y2 - y1) / l];
                const [xa, ya, xb, yb] = [x1 + ux * R, y1 + uy * R, x2 - ux * R, y2 - uy * R];
                traces.push(trait(xa, ya, xb, yb));
                if (a.oriente === true) {
                    /* La pointe de la flèche : deux traits, au bout. */
                    for (const s of [1, -1]) {
                        traces.push(trait(xb, yb, xb - ux * 11 + s * -uy * 5, yb - uy * 11 + s * ux * 5));
                    }
                }
                if (a.poids !== undefined) {
                    /* Le poids, au milieu de l'arête, décalé du côté qui s'éloigne du centre du dessin. */
                    const [mx, my] = [(x1 + x2) / 2, (y1 + y2) / 2];
                    const cote = -uy * (mx - L / 2) + ux * (my - (haut + 150)) >= 0 ? 1 : -1;
                    traces.push(texte(mx + cote * -uy * 13, my + cote * ux * 13 + 5, fr(a.poids), 'milieu', true, 14));
                }
            }
            for (const s of d.sommets) {
                const [x0, y0] = place.get(s.nom);
                traces.push({
                    type: 'polygone',
                    points: Array.from({ length: 28 }, (_, i) => {
                        const t = (i / 28) * 2 * Math.PI;
                        return [x0 + R * Math.cos(t), y0 + R * Math.sin(t)];
                    }),
                    surface: 'teinte',
                });
                traces.push(texte(x0, y0 + 5, s.nom, 'milieu', true, 14));
            }
            return rendu(H);
        }
    }
}
/* ───────────────────────────── dire un diagramme ───────────────────────────── */
const NOM_DU_TYPE = {
    bandes: 'Diagramme à bandes',
    ligne_brisee: 'Diagramme à ligne brisée',
    circulaire: 'Diagramme circulaire',
    histogramme: 'Histogramme',
    quartiles: 'Diagramme de quartiles',
    nuage: 'Nuage de points',
    arbre: 'Arbre de probabilités',
    venn: 'Diagramme de Venn',
    graphe: 'Graphe',
};
/**
 * Le diagramme dit par ses DONNÉES : « Diagramme à bandes. « Chats » : 7 ;
 * « Chiens » : à donner. » — ce que lit un lecteur d'écran d'un diagramme en
 * construction, où la description de l'auteur dirait la réponse.
 */
export function direDiagramme(d) {
    const elements = elementsDe(d)
        .map((e) => `${e.nom} : ${e.valeur === null ? 'à donner' : ecrireValeur(e.valeur)}`)
        .join(' ; ');
    /* Un graphe n'a pas d'élément : il se dit par ses arêtes. */
    const dit = d.type === 'graphe'
        ? d.aretes
            .map((a) => `${a.de} ${a.oriente === true ? 'vers' : '–'} ${a.a}${a.poids === undefined ? '' : ` (${fr(a.poids)})`}`)
            .join(' ; ')
        : elements;
    /* Plusieurs séries de quartiles : chacune est dite, par son nom. */
    const series = d.type === 'quartiles' && d.autres !== undefined
        ? `${d.nom ?? 'Première série'} : ${dit}. ` +
            d.autres
                .map((a) => `${a.nom} : le minimum : ${fr(a.min)} ; le premier quartile : ${fr(a.q1)} ; la médiane : ${fr(a.mediane)} ; le troisième quartile : ${fr(a.q3)} ; le maximum : ${fr(a.max)}`)
                .join('. ')
        : dit;
    return `${NOM_DU_TYPE[d.type]}${d.titre === undefined ? '' : ` — ${d.titre}`}. ${series}.`;
}
//# sourceMappingURL=diagramme.js.map